/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { isPlainLang } from 'shiki';

import * as prettier from 'prettier';
import prettierAngular from 'prettier/plugins/angular';
import prettierBabel from 'prettier/plugins/babel';
import prettierEsTree from 'prettier/plugins/estree';
import prettierHtml from 'prettier/plugins/html';
import prettierMarkdown from 'prettier/plugins/markdown';
import prettierPostCss from 'prettier/plugins/postcss';

import type { HighlightLang } from '../../components/HighlightCode/types';

// Map of languages that needs a dedicated parser.
const DEDICATED_LANGUAGE_PARSER: Record<string, string> = {
	'html': 'html',
	'css': 'css',
	'json': 'json',
	'vue': 'vue',
	'md': 'markdown'
};

const SELF_CLOSING_HTML_TAGS = [
	'area',
	'base',
	'br',
	'col',
	'embed',
	'hr',
	'img',
	'input',
	'link',
	'meta',
	'param',
	'source',
	'track',
	'wbr'
];

const SELF_CLOSING_REGEXP = new RegExp( `(<(?:${ SELF_CLOSING_HTML_TAGS.join( '|' ) })[^>]+)/>`, 'g' );

// A regular expression that allows removing the first level of indentation inside importmap.
const IMPORT_MAP_CONTENT_REGEXP = /(<script type="importmap">\n)(\s+\{[^<]+})(\s+<\/script>)/;

export async function formatCode( codeString: string, lang: HighlightLang ): Promise<string> {
	if ( !shouldFormatLanguage( lang ) ) {
		return Promise.resolve( codeString );
	}

	const code = await prettier.format( codeString, getPrettierConfig( lang ) );

	if ( lang === 'html' ) {
		return code
			// To remove first indentation from an object representing importmap.
			.replace( IMPORT_MAP_CONTENT_REGEXP, ( match, open, content, close ) => {
				return `${ open }${ content.replaceAll( /^\t/gm, '' ) }${ close }`;
			} )
			// To remove trailing slashes on self-closing element.s See: #637.
			.replaceAll( SELF_CLOSING_REGEXP, ( match, element ) => {
				// A closing symbol is in the same line as the opening one.
				if ( element.endsWith( ' ' ) ) {
					return `${ element.trim() }>`;
				}

				// The closing symbol isn't in the same line. Hence, do not trim indentations.
				return `${ element }>`;
			} );
	}

	return code;
}

function shouldFormatLanguage( lang: HighlightLang ) {
	if ( isPlainLang( lang ) ) {
		return false;
	}

	// Prettier does not support Bash natively. Hence, let's skip processing it.
	if ( lang === 'bash' ) {
		return false;
	}

	return true;
}

function getPrettierConfig( lang: HighlightLang ): prettier.Options {
	const defaultOptions: prettier.Options = {
		printWidth: 140,
		semi: true,
		trailingComma: 'none',
		arrowParens: 'avoid',
		bracketSpacing: true,
		singleQuote: true,
		useTabs: true,
		parser: lang in DEDICATED_LANGUAGE_PARSER ? DEDICATED_LANGUAGE_PARSER[ lang ] : 'babel-ts',
		plugins: getPrettierPlugins( lang )
	};

	// To avoid formatting the entire file when executing a command that would modify its content,
	// e,g., `npm install --save`.
	if ( lang === 'json' ) {
		return {
			...defaultOptions,
			tabWidth: 2,
			useTabs: false
		};
	}

	return defaultOptions;
}

function getPrettierPlugins( lang: HighlightLang ): Array<prettier.Plugin> {
	if ( lang === 'html' ) {
		return [ prettierHtml ];
	}

	if ( lang === 'css' ) {
		return [ prettierPostCss ];
	}

	if ( lang === 'vue' ) {
		return [ prettierHtml, prettierBabel, prettierPostCss, prettierEsTree ];
	}

	if ( lang === 'angular-ts' ) {
		return [ prettierAngular, prettierBabel, prettierEsTree ];
	}

	if ( lang === 'md' ) {
		return [ prettierMarkdown ];
	}

	return [ prettierBabel, prettierEsTree ];
}
